<script setup lang="ts">
/**
 * Rendu d'un corps de message inbox/notification avec icônes inline.
 * Parse le markup [[res:code|Label]] / [[item:..]] / [[img:folder/code|..]] /
 * [[planet:id|Nom]] / [[fleet:id|Nom]] (cf utils/messageMarkup) et affiche
 * une icône + le libellé pour chaque jeton, le reste en texte (sauts de
 * ligne préservés). Enregistré globalement (main.ts) -> <MessageBody>.
 */
import { computed } from 'vue';
import { parseMessageMarkup } from '@/utils/messageMarkup';

const props = defineProps<{
  /** Corps brut du message (avec markup éventuel). */
  text: string | null | undefined;
}>();

const segments = computed(() => parseMessageMarkup(props.text));
</script>

<template>
  <span class="message-body">
    <template v-for="(seg, i) in segments" :key="i">
      <span v-if="seg.kind === 'text'" class="mb-text">{{ seg.text }}</span>
      <span
        v-else-if="seg.image"
        class="mb-chip"
        :class="`mb-chip--${seg.kind}`"
      >
        <img
          class="mb-chip__icon"
          :src="seg.image"
          :alt="seg.text"
          v-img-hide-on-error
        />
        <span class="mb-chip__label">{{ seg.text }}</span>
      </span>
      <span v-else class="mb-chip mb-chip--ref" :class="`mb-chip--${seg.kind}`">
        <span class="mb-chip__label">{{ seg.text }}</span>
      </span>
    </template>
  </span>
</template>

<style scoped lang="scss">
.message-body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
}

.mb-text {
  white-space: pre-wrap;
}

.mb-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px 1px 3px;
  margin: 0 1px;
  border-radius: 10px;
  background: rgba($accent-primary, 0.12);
  border: 1px solid rgba($accent-primary, 0.28);
  vertical-align: middle;
  font-weight: 600;
  white-space: nowrap;

  &--ref {
    padding-left: 7px;
  }

  &--planet {
    background: rgba($accent-positive, 0.12);
    border-color: rgba($accent-positive, 0.28);
  }

  &--fleet {
    background: rgba($accent-tech, 0.12);
    border-color: rgba($accent-tech, 0.28);
  }

  &__icon {
    width: 18px;
    height: 18px;
    object-fit: contain;
    border-radius: 3px;
  }

  &__label {
    font-size: 0.92em;
  }
}
</style>
